import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { type ColumnDef } from '@tanstack/react-table'
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FileText,
  LineChart,
  Loader2,
  Play,
  Save,
} from 'lucide-react'
import { AppShell } from '../../components/AppShell'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { ObjectPicker } from '../../components/ObjectPicker'
import { DateRangeSelect } from '../../components/DateRangeSelect'
import { CurrencySelect } from '../../components/CurrencySelect'
import { SearchInput } from '../../components/SearchInput'
import { SwitchField } from '../../components/SwitchField'
import { AdvancedSection } from '../../components/AdvancedSection'
import { FieldLabel } from '../../components/FieldLabel'
import { TypeBadge } from '../../components/TargetingTotalsTable'
import { TimeSeriesChart } from '../../components/reports/TimeSeriesChart'
import { SavedQueriesMenu } from '../../components/reports/SavedQueriesMenu'
import { SaveQueryDialog } from '../../components/reports/SaveQueryDialog'
import { useSavedQueries, type SavedQuery, type SavedQueryInput } from '../../hooks/useSavedQueries'
import { toast } from 'sonner'
import { catalogMetrics, formatStatValue, statMetricColumns, type StatMetric } from '../../components/statsColumns'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { useNetwork } from '../../contexts/NetworkContext'
import { usePersistedState } from '../../hooks/usePersistedState'
import { StatsApi } from '@/services/stats/statsApi'
import { SearchApi } from '@/services/core/searchApi'
import { logPosthogInfo } from '@/services/core/posthogLog'
import { getDateRange, type DateRange } from '@/services/stats/statsRange'
import {
  breakdownOptions,
  isTimeBreakdown,
  OBJECT_TYPES_BY_SIDE,
  QUERY_OBJECT_TYPES,
  subBreakdownOptions,
  TARGETING_BREAKDOWN,
  toStatsParams,
  type Breakdown,
  type PoliticalFilter,
  type QueryDefinition,
  type QueryObjectType,
  type QuerySide,
  type SubBreakdown,
  type TimeBreakdown,
} from '@/services/stats/statsQuery'
import {
  readStatsResult,
  readTargetingResult,
  toTimeSeries,
  type QueryResult,
  type QueryRow,
  type ResultKind,
} from '@/services/stats/statsQueryResult'
import { exportToCSV, exportToExcel } from '../../utils/importExport'
import { formatDate, formatDateTime, formatNumber } from '../../utils/format'
import { useStatLabel } from '../../hooks/useStatLabel'

const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

// ─── Metrics ──────────────────────────────────────────────────────────────────

/**
 * Columns per kind of result, the everyday ones shown and the rest in the
 * column picker. A publisher's `cost` is what it earned, so it is named that.
 */
const METRICS: Record<ResultKind, { visible: string[]; rest: string[] }> = {
  advertising: {
    visible: ['bids', 'impressions', 'rendered', 'viewables', 'viewability', 'uniqueUsers', 'clicks', 'ctr', 'cost'],
    rest: ['visibles', 'visibility', 'renderedRate', 'fullyViewables', 'conversions', 'impressionsPerUniqueUser', 'eCpm', 'grossECpm', 'vCpm', 'grossVCpm', 'cpc', 'grossCpc', 'totalCost'],
  },
  publishing: {
    visible: ['impressionRequests', 'impressions', 'rendered', 'viewables', 'viewability', 'clicks', 'ctr', 'eCpm', 'cost'],
    rest: ['positiveImpressionRequests', 'matchRate', 'visibles', 'visibility', 'uniqueUsers', 'impressionsPerUniqueUser', 'revenueShare'],
  },
  targeting: {
    visible: ['impressions', 'rendered', 'viewables', 'clicks', 'cost'],
    rest: ['visibles'],
  },
}

function metricsFor(kind: ResultKind): StatMetric[] {
  const { visible, rest } = METRICS[kind]
  return catalogMetrics(visible, rest).map((m) =>
    kind === 'publishing' && m.key === 'cost' ? { ...m, labelKey: 'stats.earnings' } : m
  )
}

// ─── Labels ───────────────────────────────────────────────────────────────────

const BREAKDOWN_LABEL_KEYS: Record<Breakdown, string> = {
  MONTHLY: 'queries.breakdowns.month',
  WEEKLY: 'queries.breakdowns.week',
  DAILY: 'queries.breakdowns.day',
  HOURLY: 'queries.breakdowns.hour',
  ADVERTISER: 'queries.breakdowns.advertiser',
  ORDER: 'queries.breakdowns.order',
  LINE_ITEM: 'queries.breakdowns.lineItem',
  CREATIVE: 'queries.breakdowns.creative',
  EARNINGS_ACCOUNT: 'queries.breakdowns.earningsAccount',
  SITE: 'queries.breakdowns.site',
  AD_UNIT: 'queries.breakdowns.adUnit',
  TEAM: 'queries.breakdowns.team',
  TIER: 'queries.breakdowns.tier',
  MARKETPLACE_PRODUCT: 'queries.breakdowns.marketplaceProduct',
  [TARGETING_BREAKDOWN]: 'queries.breakdowns.targeting',
}

const OBJECT_ROUTES: Record<QueryObjectType, string> = {
  advertiser: '/advertising/advertisers',
  order: '/advertising/orders',
  lineItem: '/advertising/lineitems',
  creative: '/advertising/creatives',
  earningsAccount: '/publishing/earnings',
  site: '/publishing/sites',
  adUnit: '/publishing/ad-units',
}

/** A time bucket as the table and tooltip name it, in the network's zone. */
function formatBucket(iso: string, unit: TimeBreakdown, timeZone: string, t: TFunction): string {
  if (unit === 'MONTHLY') return formatDate(iso, { month: 'long', year: 'numeric', timeZone })
  if (unit === 'WEEKLY') {
    return t('queries.weekOf', { date: formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric', timeZone }) })
  }
  if (unit === 'DAILY') {
    return formatDate(iso, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone })
  }
  return formatDateTime(iso, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone })
}

/** The same bucket as a short axis tick. */
function formatTick(iso: string, unit: TimeBreakdown, timeZone: string): string {
  if (unit === 'MONTHLY') return formatDate(iso, { month: 'short', year: '2-digit', timeZone })
  if (unit === 'HOURLY') return formatDateTime(iso, { hour: '2-digit', minute: '2-digit', timeZone })
  return formatDate(iso, { day: 'numeric', month: 'short', timeZone })
}

// ─── Page ─────────────────────────────────────────────────────────────────────

interface Picked {
  id: string
  name?: string
}

interface QuickReportPageProps {
  mode: QuerySide
}

/**
 * Ad-hoc stats: pick what to report on, how to break it down, and run it. One
 * page for every object type — the breakdowns on offer follow the type, so a
 * publisher can be split by the advertisers it carried and an advertiser by
 * the sites it ran on, but never the other way round.
 */
export function QuickReportPage({ mode }: QuickReportPageProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const marketplace = !!currentNetwork?.marketplacePlatform
  // As the old UI has it: any network that can run political ads, not only
  // the ones with the political app switched on.
  const showPolitical =
    !!currentNetwork?.politicalApp ||
    !!currentNetwork?.marketplacePlatform ||
    !!currentNetwork?.capabilities.includes('POLITICAL_ADS')

  // ── The query being built ──────────────────────────────────────────────────
  // Each module reports on its own side only: advertisers and what they buy
  // under advertising, publishers and their inventory under publishing.
  const objectTypes = OBJECT_TYPES_BY_SIDE[mode]
  const [storedType, setObjectType] = usePersistedState<QueryObjectType>(
    `queries.${mode}.objectType`,
    objectTypes[0]
  )
  const objectType = objectTypes.includes(storedType) ? storedType : objectTypes[0]
  const [breakdown, setBreakdown] = useState<Breakdown>(() => QUERY_OBJECT_TYPES[objectType].breakdown)
  const [subBreakdown, setSubBreakdown] = useState<SubBreakdown | null>(null)
  const [objects, setObjects] = useState<Picked[]>([])
  const [parents, setParents] = useState<ParentPicks>({})
  const parentTypes = QUERY_OBJECT_TYPES[objectType].parents
  const parentsPicked = parentTypes.some((type) => parents[type]?.length)
  const [range, setRange] = usePersistedState<DateRange>(`queries.${mode}.range`, '30d')
  const [currency, setCurrency] = useState('')
  const [political, setPolitical] = useState<PoliticalFilter>('all')
  const [includeAll, setIncludeAll] = useState(false)
  const [advanced, setAdvanced] = useState(false)

  const advancedCount = [objects.length > 0, parentsPicked, includeAll].filter(Boolean).length

  // Never empty: a currency the network no longer offers, or none picked yet,
  // falls back to the first one it does.
  const availableCurrencies = currentNetwork?.availableCurrencies ?? []
  const effectiveCurrency = availableCurrencies.includes(currency)
    ? currency
    : availableCurrencies[0] || currentNetwork?.defaultCurrency || 'EUR'
  const options = breakdownOptions(objectType, marketplace)
  const subOptions = subBreakdownOptions(objectType, breakdown, marketplace)

  // Switching type starts over: last type's objects and breakdowns may mean
  // nothing for this one.
  const changeObjectType = (next: QueryObjectType) => {
    setObjectType(next)
    setBreakdown(QUERY_OBJECT_TYPES[next].breakdown)
    setSubBreakdown(null)
    setObjects([])
    setParents({})
  }

  // A split that the new breakdown does not offer is dropped, not kept hidden.
  const changeBreakdown = (next: Breakdown) => {
    setBreakdown(next)
    if (subBreakdown && !subBreakdownOptions(objectType, next, marketplace).includes(subBreakdown)) {
      setSubBreakdown(null)
    }
  }

  const label = (b: Breakdown | SubBreakdown) => t(BREAKDOWN_LABEL_KEYS[b])
  const typeLabel = (type: QueryObjectType, count = 2) => t(`queries.types.${type}`, { count })

  const breakdownGroups: { label: string; options: Breakdown[] }[] = [
    { label: t('queries.sections.time'), options: options.time },
    { label: t('queries.sections.own'), options: options.own },
    { label: t(mode === 'advertising' ? 'queries.sections.inventory' : 'queries.sections.demand'), options: options.across },
  ]

  // ── Running it ─────────────────────────────────────────────────────────────
  // The results stay on the query that produced them; editing the form marks
  // them out of date rather than silently re-running a heavy query.
  const [ran, setRan] = useState<{ query: QueryDefinition; run: number } | null>(null)

  const current: SavedQueryInput = {
    name: '',
    objectType,
    breakdown,
    subBreakdown,
    objects,
    parents,
    range,
    currency: effectiveCurrency,
    political,
    includeAll,
  }

  const run = (query: SavedQueryInput = current) => {
    const { startDate, endDate } = getDateRange(query.range, timeZone)
    setRan((prev) => ({
      run: (prev?.run ?? 0) + 1,
      query: {
        objectType: query.objectType,
        breakdown: query.breakdown,
        subBreakdown: query.subBreakdown,
        objectIds: query.objects.map((o) => o.id),
        parentIds: toParentIds(query.parents),
        startDate,
        endDate,
        currency: availableCurrencies.includes(query.currency) ? query.currency : effectiveCurrency,
        political: query.political,
        includeAll: query.includeAll,
      },
    }))
  }

  // ── Saved queries ──────────────────────────────────────────────────────────
  const savedQueries = useSavedQueries(mode)
  const [saveOpen, setSaveOpen] = useState(false)
  const [loadedId, setLoadedId] = useState<string | null>(null)
  const loaded = savedQueries.saved.find((q) => q.id === loadedId)

  // Loading fills in every choice and runs it straight away — the point of
  // saving one is not having to set it up again.
  const load = (query: SavedQuery) => {
    // A query saved before a rule changed may no longer be valid here.
    if (!objectTypes.includes(query.objectType)) return
    const breakdowns = breakdownOptions(query.objectType, marketplace)
    const validBreakdown = [...breakdowns.time, ...breakdowns.own, ...breakdowns.across].includes(query.breakdown)
      ? query.breakdown
      : QUERY_OBJECT_TYPES[query.objectType].breakdown
    const validSub =
      query.subBreakdown && subBreakdownOptions(query.objectType, validBreakdown, marketplace).includes(query.subBreakdown)
        ? query.subBreakdown
        : null
    const next = { ...query, breakdown: validBreakdown, subBreakdown: validSub }
    setObjectType(next.objectType)
    setBreakdown(next.breakdown)
    setSubBreakdown(next.subBreakdown)
    setObjects(next.objects)
    setParents(next.parents ?? {})
    setRange(next.range)
    setCurrency(next.currency)
    setPolitical(next.political)
    setIncludeAll(next.includeAll)
    setLoadedId(query.id)
    run(next)
  }

  // A saved report can be opened straight from the command palette, as
  // `?saved=<id>`. The parameter is cleared once applied so later edits to the
  // form are not undone by it.
  const [searchParams, setSearchParams] = useSearchParams()
  const savedParam = searchParams.get('saved')

  useEffect(() => {
    if (!savedParam || savedQueries.isLoading) return
    const query = savedQueries.saved.find((q) => q.id === savedParam)
    if (query) load(query)
    setSearchParams(
      (params) => {
        params.delete('saved')
        return params
      },
      { replace: true },
    )
  }, [savedParam, savedQueries.isLoading])

  const saveCurrent = async (name: string) => {
    try {
      const saved = await savedQueries.save({ ...current, name })
      if (posthogConfigured) posthog.capture('quick_report_saved', {
        mode,
        object_type: current.objectType,
        breakdown: current.breakdown,
        has_sub_breakdown: Boolean(current.subBreakdown),
      })
      logPosthogInfo('quick report saved', {
        event: 'quick_report_saved',
        report_mode: mode,
        object_type: current.objectType,
        breakdown: current.breakdown,
        has_sub_breakdown: Boolean(current.subBreakdown),
      })
      setLoadedId(saved.find((q) => q.name === name && q.side === mode)?.id ?? null)
      setSaveOpen(false)
      toast.success(t('queries.saved.savedToast', { name }))
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  const deleteSaved = async (query: SavedQuery) => {
    try {
      await savedQueries.remove(query.id)
      if (loadedId === query.id) setLoadedId(null)
      toast.success(t('queries.saved.deletedToast', { name: query.name }))
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  const { data: result, isFetching, error } = useQuery({
    queryKey: ['quickReport', ran?.run, ran?.query],
    queryFn: async (): Promise<QueryResult> => {
      const query = ran!.query
      const params = toStatsParams(query)
      if (query.breakdown === TARGETING_BREAKDOWN) return readTargetingResult(await StatsApi.getTargeting(params))
      return readStatsResult(await StatsApi.get(params), query.breakdown, query.subBreakdown)
    },
    enabled: !!ran,
    staleTime: Infinity,
  })

  const dirty =
    !!ran &&
    (ran.query.objectType !== objectType ||
      ran.query.breakdown !== breakdown ||
      ran.query.subBreakdown !== subBreakdown ||
      ran.query.objectIds.join() !== objects.map((o) => o.id).join() ||
      parentTypes.some((type) => (ran.query.parentIds[type] ?? []).join() !== (parents[type] ?? []).map((o) => o.id).join()) ||
      ran.query.currency !== effectiveCurrency ||
      ran.query.political !== political ||
      ran.query.includeAll !== includeAll)

  return (
    <AppShell
      crumbs={[
        { label: mode === 'advertising' ? t('nav.adServing') : t('nav.publishing'), href: `/${mode}` },
        { label: t('nav.quickReports') },
      ]}
    >
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('nav.quickReports')}</h1>
            <p>{t('queries.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <SavedQueriesMenu
              saved={savedQueries.saved}
              loading={savedQueries.isLoading}
              onLoad={load}
              onDelete={deleteSaved}
            />
            <Button variant="outline" size="sm" onClick={() => setSaveOpen(true)}>
              <Save className="size-4" />
              {t('queries.saved.saveButton')}
            </Button>
          </div>
        </div>

        <SaveQueryDialog
          open={saveOpen}
          onOpenChange={setSaveOpen}
          defaultName={loaded?.name ?? ''}
          existingNames={savedQueries.saved.map((q) => q.name)}
          saving={savedQueries.isSaving}
          onSave={saveCurrent}
        />

        <Card className="mb-4">
          <CardContent className="flex flex-col gap-6 pt-6">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-2">
                <Label>{t('queries.reportOn')}</Label>
                <Select value={objectType} onValueChange={(v) => changeObjectType(v as QueryObjectType)}>
                  <SelectTrigger className="w-48">
                    <SelectValue>{typeLabel(objectType)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {objectTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {typeLabel(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <FieldLabel
                  hint={t('common.optional')}
                  tooltip={t('queries.breakDownByHelp', { type: typeLabel(objectType, 1).toLowerCase() })}
                >
                  {t('queries.breakDownBy')}
                </FieldLabel>
                <Select value={breakdown} onValueChange={(v) => changeBreakdown(v as Breakdown)}>
                  <SelectTrigger className="w-48">
                    <SelectValue>{label(breakdown)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {breakdownGroups.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel>{group.label}</SelectLabel>
                        {group.options.map((b) => (
                          <SelectItem key={b} value={b}>
                            {label(b)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {subOptions.length > 0 && (
                <div className="flex flex-col gap-2">
                  <FieldLabel hint={t('common.optional')} tooltip={t('queries.thenByHelp')}>
                    {t('queries.thenBy')}
                  </FieldLabel>
                  <Select
                    value={subBreakdown ?? 'NONE'}
                    onValueChange={(v) => setSubBreakdown(v === 'NONE' ? null : (v as SubBreakdown))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue>{subBreakdown ? label(subBreakdown) : t('queries.noSplit')}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">{t('queries.noSplit')}</SelectItem>
                      {subOptions.map((b) => (
                        <SelectItem key={b} value={b}>
                          {label(b)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Label>{t('queries.period')}</Label>
                <DateRangeSelect value={range} onChange={setRange} className="h-9 w-auto min-w-40" />
              </div>
              <div className="flex flex-col gap-2">
                <Label>{t('reports.currency')}</Label>
                <CurrencySelect value={effectiveCurrency} onValueChange={setCurrency} />
              </div>
              {showPolitical && (
                <div className="flex flex-col gap-2">
                  <Label>{t('queries.political.label')}</Label>
                  <Select value={political} onValueChange={(v) => setPolitical(v as PoliticalFilter)}>
                    <SelectTrigger className="w-48">
                      <SelectValue>{t(`queries.political.${political}`)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {(['all', 'only', 'exclude'] as const).map((p) => (
                        <SelectItem key={p} value={p}>
                          {t(`queries.political.${p}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Most reports cover every object; narrowing them down is kept out
                of the way. A changed setting is counted on the button, so a
                closed section never hides a choice in effect. */}
            <AdvancedSection open={advanced} onOpenChange={setAdvanced} activeCount={advancedCount}>
              <div className="flex flex-col gap-2 md:w-1/2">
                <Label>{typeLabel(objectType)}</Label>
                <ObjectPicker<Picked>
                  queryKey={`queryObjects.${objectType}`}
                  search={async (q) =>
                    (await SearchApi.search({ q, types: QUERY_OBJECT_TYPES[objectType].searchType, pageSize: 20 }))
                      .searchResults
                  }
                  selected={objects}
                  onChange={setObjects}
                  addLabel={t('queries.pick', { type: typeLabel(objectType).toLowerCase() })}
                  emptyText={t(parentsPicked ? 'queries.onlyFromParents' : 'queries.allObjects', {
                    type: typeLabel(objectType).toLowerCase(),
                  })}
                />
              </div>
              {parentTypes.length > 0 && (
                <div className="flex flex-col gap-4 md:w-1/2">
                  {parentTypes.map((parent) => (
                    <div key={parent} className="flex flex-col gap-2">
                      <FieldLabel
                        hint={t('common.optional')}
                        tooltip={t('queries.fromParentHelp', {
                          type: typeLabel(objectType).toLowerCase(),
                          parent: typeLabel(parent).toLowerCase(),
                        })}
                      >
                        {t('queries.fromParent', { type: typeLabel(objectType), parent: typeLabel(parent).toLowerCase() })}
                      </FieldLabel>
                      <ObjectPicker<Picked>
                        queryKey={`queryObjects.${parent}`}
                        search={async (q) =>
                          (await SearchApi.search({ q, types: QUERY_OBJECT_TYPES[parent].searchType, pageSize: 20 }))
                            .searchResults
                        }
                        selected={parents[parent] ?? []}
                        onChange={(picked) => setParents((prev) => ({ ...prev, [parent]: picked }))}
                        addLabel={t('queries.pick', { type: typeLabel(parent).toLowerCase() })}
                        emptyText={t('queries.noParents', { parent: typeLabel(parent).toLowerCase() })}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className="md:w-1/2">
                <SwitchField
                  label={t('queries.includeAll', { type: typeLabel(objectType).toLowerCase() })}
                  description={t('queries.includeAllHelp')}
                  checked={includeAll}
                  onCheckedChange={setIncludeAll}
                />
              </div>
            </AdvancedSection>

            <div className="flex flex-wrap items-center gap-3 border-t pt-4">
              <Button
                onClick={() => {
                  if (posthogConfigured) posthog.capture('quick_report_run', {
                    mode,
                    object_type: objectType,
                    breakdown,
                    has_sub_breakdown: Boolean(subBreakdown),
                    selected_object_count: objects.length,
                    has_parent_filter: parentsPicked,
                    date_range: range,
                  })
                  logPosthogInfo('quick report requested', {
                    event: 'quick_report_requested',
                    report_mode: mode,
                    object_type: objectType,
                    breakdown,
                    has_sub_breakdown: Boolean(subBreakdown),
                    selected_object_count: objects.length,
                    has_parent_filter: parentsPicked,
                    date_range: range,
                  })
                  run()
                }}
                disabled={isFetching}
              >
                {isFetching ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
                {isFetching ? t('queries.running') : t('queries.run')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {describeQuery(
                  t,
                  typeLabel(objectType),
                  objects.length,
                  parentTypes.flatMap((type) => {
                    const count = parents[type]?.length ?? 0
                    return count ? [{ label: typeLabel(type, count), count }] : []
                  }),
                  label(breakdown),
                  subBreakdown && label(subBreakdown)
                )}
              </span>
              {dirty && !isFetching && (
                <span className="text-xs text-(--ad-warning)">{t('queries.outdated')}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {!ran ? (
          <Card>
            <CardContent className="py-10">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <BarChart3 />
                  </EmptyMedia>
                  <EmptyTitle>{t('queries.emptyTitle')}</EmptyTitle>
                  <EmptyDescription>{t('queries.emptyDescription')}</EmptyDescription>
                </EmptyHeader>
              </Empty>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="py-6 text-sm text-(--ad-danger)">{(error as Error).message}</CardContent>
          </Card>
        ) : (
          <QueryResults query={ran.query} result={result} loading={isFetching} timeZone={timeZone} />
        )}
      </div>
    </AppShell>
  )
}

/** Parents picked per type, as they are kept and saved. */
type ParentPicks = Partial<Record<QueryObjectType, Picked[]>>

function toParentIds(parents: ParentPicks = {}): QueryDefinition['parentIds'] {
  return Object.fromEntries(Object.entries(parents).map(([type, picked]) => [type, picked.map((o) => o.id)]))
}

/**
 * "All advertisers, by day, then by line item" — the query read back as a
 * sentence. Picked parents join in: "2 selected line items and line items
 * under 3 orders, by day".
 */
function describeQuery(
  t: TFunction,
  type: string,
  picked: number,
  parents: { label: string; count: number }[],
  breakdown: string,
  subBreakdown: string | null
): string {
  const parts = [
    ...(picked ? [t('queries.summary.picked', { count: picked, type: type.toLowerCase() })] : []),
    ...parents.map((p) => t('queries.summary.under', { type: type.toLowerCase(), count: p.count, parent: p.label.toLowerCase() })),
  ]
  const joined = parts.join(t('queries.summary.and'))
  const subject = parts.length
    ? joined.charAt(0).toUpperCase() + joined.slice(1)
    : t('queries.summary.all', { type: type.toLowerCase() })
  const by = t('queries.summary.by', { breakdown: breakdown.toLowerCase() })
  const then = subBreakdown ? t('queries.summary.then', { breakdown: subBreakdown.toLowerCase() }) : ''
  return `${subject}, ${by}${then ? `, ${then}` : ''}`
}

// ─── Results ──────────────────────────────────────────────────────────────────

interface QueryResultsProps {
  query: QueryDefinition
  result: QueryResult | undefined
  loading: boolean
  timeZone: string
}

function QueryResults({ query, result, loading, timeZone }: QueryResultsProps) {
  const { t } = useTranslation()
  const statLabel = useStatLabel()
  const [search, setSearch] = useState('')
  const kind: ResultKind = result?.kind ?? (QUERY_OBJECT_TYPES[query.objectType].side === 'advertising' ? 'advertising' : 'publishing')
  const metrics = useMemo(() => metricsFor(kind), [kind])
  const { breakdown, subBreakdown } = query

  useEffect(() => setSearch(''), [query])

  /** What a row is called: its object, its time bucket, or what it stands for. */
  const nameOf = useMemo(() => {
    return (row: QueryRow, level: Breakdown | SubBreakdown): string => {
      if (row.isOthers) return t('stats.allOthers')
      if (row.time && isTimeBreakdown(level)) return formatBucket(row.time, level, timeZone, t)
      if (row.name) return row.name
      return t('queries.noneOf', { breakdown: t(BREAKDOWN_LABEL_KEYS[level]).toLowerCase() })
    }
  }, [t, timeZone])

  // A search keeps a group when any row in it matches, showing only those rows.
  const rows = useMemo(() => {
    const all = result?.rows ?? []
    const q = search.trim().toLowerCase()
    if (!q) return all
    const matches = (row: QueryRow, level: Breakdown | SubBreakdown) => nameOf(row, level).toLowerCase().includes(q)
    return all.flatMap((row) => {
      if (matches(row, breakdown)) return [row]
      const subRows = subBreakdown ? (row.subRows ?? []).filter((s) => matches(s, subBreakdown)) : []
      return subRows.length ? [{ ...row, subRows }] : []
    })
  }, [result, search, nameOf, breakdown, subBreakdown])

  const columns = useMemo<ColumnDef<QueryRow, unknown>[]>(() => {
    const nameColumn: ColumnDef<QueryRow, unknown> = {
      id: 'name',
      header: subBreakdown
        ? `${t(BREAKDOWN_LABEL_KEYS[breakdown])} / ${t(BREAKDOWN_LABEL_KEYS[subBreakdown])}`
        : t(BREAKDOWN_LABEL_KEYS[breakdown]),
      enableHiding: false,
      meta: { minWidth: 240 },
      // Time buckets sort by when they are, not by how their name spells.
      accessorFn: (row) => row.time ?? row.name,
      cell: ({ row }) => {
        const level = row.depth === 0 ? breakdown : (subBreakdown ?? breakdown)
        const data = row.original
        const name = nameOf(data, level)
        return (
          <div className={cn('flex items-center gap-2', row.depth > 0 && 'pl-9')}>
            {row.getCanExpand() && (
              <Button
                variant="ghost"
                size="icon-sm"
                className="-my-1"
                aria-label={row.getIsExpanded() ? t('queries.collapse') : t('queries.expand')}
                onClick={row.getToggleExpandedHandler()}
              >
                {row.getIsExpanded() ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              </Button>
            )}
            {data.targetingType && <TypeBadge type={data.targetingType} />}
            {data.objectType && data.objectId ? (
              <Link to={`${OBJECT_ROUTES[data.objectType]}/${data.objectId}`} className="font-semibold hover:underline">
                {name}
              </Link>
            ) : (
              <span className={data.isOthers || !data.name ? 'text-(--ad-text-muted)' : undefined}>{name}</span>
            )}
          </div>
        )
      },
      footer: () => t('queries.total'),
    }
    const stats = statMetricColumns<QueryRow>({
      metrics,
      statsFor: (row) => row.stats,
      label: statLabel,
      totals: result?.kind === 'targeting' ? undefined : result?.totals,
    })
    return [nameColumn, ...stats]
  }, [t, statLabel, metrics, breakdown, subBreakdown, nameOf, result])

  // ── Chart ──────────────────────────────────────────────────────────────────
  const hasTime = isTimeBreakdown(breakdown)
  const chartMetrics = metrics.filter((m) => m.defaultVisible)
  const [chartMetric, setChartMetric] = useState('impressions')
  const metric = chartMetrics.find((m) => m.key === chartMetric) ?? chartMetrics.find((m) => m.key === 'impressions')!

  const series = useMemo(() => {
    if (!result || !hasTime) return null
    const additive = metric.format === 'count' || metric.format === 'currency'
    return toTimeSeries(result, breakdown, subBreakdown, metric.key, additive, (row) =>
      nameOf(row, subBreakdown ?? breakdown)
    )
  }, [result, hasTime, metric, breakdown, subBreakdown, nameOf])

  const timeUnit = breakdown as TimeBreakdown
  const formatMetric = (value: number) =>
    formatStatValue(
      metric.format === 'currency' ? { amount: value, currency: query.currency } : value,
      metric.format
    ) ?? ''

  // ── Export ─────────────────────────────────────────────────────────────────
  // Targeting rows overlap, so their figures have no meaningful total.
  const exportTotals = result?.kind === 'targeting' ? undefined : result?.totals

  const download = (format: 'csv' | 'excel' | 'excelTotals') => {
    const first = t(BREAKDOWN_LABEL_KEYS[breakdown])
    const second = subBreakdown ? t(BREAKDOWN_LABEL_KEYS[subBreakdown]) : null
    const values = (stats: QueryRow['stats']) =>
      Object.fromEntries(
        metrics.map((m) => {
          const v = stats[m.key]
          const n = typeof v === 'object' && v && 'amount' in v ? (v as { amount: number }).amount : v
          return [statLabel(m.labelKey), n ?? '']
        })
      )
    const flat = rows.flatMap((row) =>
      second && row.subRows
        ? row.subRows.map((sub) => ({
            [first]: nameOf(row, breakdown),
            [second]: nameOf(sub, subBreakdown!),
            ...values(sub.stats),
          }))
        : [
            {
              ...(row.targetingType ? { [t('stats.targetingType')]: row.targetingType } : {}),
              [first]: nameOf(row, breakdown),
              ...values(row.stats),
            },
          ]
    )
    if (!flat.length) return
    const filename = [query.objectType, breakdown, subBreakdown, query.startDate.slice(0, 10), query.endDate.slice(0, 10)]
      .filter(Boolean)
      .join('-')
      .toLowerCase()
    if (format === 'csv') exportToCSV(flat, filename)
    else if (format === 'excel' || !exportTotals) void exportToExcel(flat, filename)
    else {
      const totals = { [Object.keys(flat[0])[0]]: t('queries.total'), ...values(exportTotals) }
      void exportToExcel(flat, filename, {}, undefined, undefined, totals)
    }
    if (posthogConfigured) posthog.capture('quick_report_exported', {
      format,
      object_type: query.objectType,
      breakdown,
      has_sub_breakdown: Boolean(subBreakdown),
      row_count: flat.length,
    })
  }

  const rowCount = rows.reduce((n, r) => n + (r.subRows?.length ?? 1), 0)

  return (
    <div className="flex flex-col gap-4">
      {hasTime && (
        <Card tone="insight">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
            <CardTitle>
              <CardIcon icon={LineChart} />
              {t('queries.chartTitle', { metric: statLabel(metric.labelKey), unit: t(BREAKDOWN_LABEL_KEYS[timeUnit]).toLowerCase() })}
            </CardTitle>
            <Select value={metric.key} onValueChange={setChartMetric}>
              <SelectTrigger size="sm" className="w-44" aria-label={t('queries.chartMetric')}>
                <SelectValue>{statLabel(metric.labelKey)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {chartMetrics.map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {statLabel(m.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loading || !series ? (
              <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
              </div>
            ) : series.points.length === 0 ? (
              <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
                {t('queries.noResults')}
              </div>
            ) : (
              <TimeSeriesChart
                data={series}
                metricLabel={statLabel(metric.labelKey)}
                otherLabel={t('queries.other')}
                formatValue={formatMetric}
                formatAxisValue={(v) =>
                  metric.format === 'count' || metric.format === 'currency'
                    ? formatNumber(v, { notation: 'compact', maximumFractionDigits: 1 })
                    : formatMetric(v)
                }
                formatTick={(iso) => formatTick(iso, timeUnit, timeZone)}
                formatTime={(iso) => formatBucket(iso, timeUnit, timeZone, t)}
              />
            )}
          </CardContent>
        </Card>
      )}

      <ListCard tone="insight">
        <ListCardHeader
          title={
            <>
              <CardIcon icon={BarChart3} />
              {t('queries.results')}
            </>
          }
          count={loading ? 0 : rowCount}
          subtitle={`${formatDateTime(query.startDate, { dateStyle: 'medium', timeStyle: 'short', timeZone })} – ${formatDateTime(query.endDate, { dateStyle: 'medium', timeStyle: 'short', timeZone })}`}
          controls={<SearchInput value={search} onChange={setSearch} placeholder={t('queries.filter')} />}
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={loading || !rows.length}>
                  <Download className="size-4" />
                  {t('reports.download')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => download('excel')}>
                  <FileSpreadsheet className="size-4" />
                  Excel (.xlsx)
                </DropdownMenuItem>
                {exportTotals && (
                  <DropdownMenuItem onClick={() => download('excelTotals')}>
                    <FileSpreadsheet className="size-4" />
                    {t('importExport.excelWithTotals')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => download('csv')}>
                  <FileText className="size-4" />
                  CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />
        <DataTable
          // Remounted per run, so sorting and expansion start fresh with the new shape.
          key={`${breakdown}/${subBreakdown}/${kind}`}
          columns={columns}
          data={rows}
          loading={loading}
          skeletonRows={8}
          getRowId={(row) => row.id}
          getSubRows={subBreakdown ? (row) => row.subRows : undefined}
          columnsKey={`queries.${kind}`}
          emptyMessage={t('queries.noResults')}
          inset
        />
      </ListCard>
    </div>
  )
}
